'use client';

import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { BookMarked, ChevronLeft, ChevronRight, List, Trash2 } from 'lucide-react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import { api } from '@/utils/api';
import { parseContinueReadingList } from '@/pages/colecao/collection-helpers';
import Pagination from '@/components/ui/Pagination';
import { accentHexForFormato, HOME_FORMATO_PADRAO, rgbaFromHex } from '@/constants/obras-generos';
import { applyFallbackImageOnError, FALLBACK_IMAGE_URL, getObraImageUrl } from '@/utils/image-utils';
import { generateChapterLink, generateWorkLink } from '@/utils/utils';

const HOME_ROW_LIMIT_NARROW = 10;
const HOME_ROW_LIMIT_WIDE = 24;
const SHOW_ALL_LIMITE = 30;

const navBtnClass =
    'cursor-pointer p-2 rounded-lg border border-white/10 bg-neutral-900/40 text-white/50 shadow-sm transition-all duration-200 hover:scale-110 hover:border-white/20 hover:bg-neutral-900/60 hover:text-white active:scale-95';

interface ContinueLendoProps {
    formatoId?: number;
    /** Lista completa (página `/continue-lendo`) — apagar leitura, swipe, paginação. */
    showAll?: boolean;
    searchQuery?: string;
    pagina?: number;
}

type CapituloResumo = {
    cap_id?: number;
    cap_num?: number | string;
    numero?: number | string;
    cap_nome?: string;
};

type ContinueItemShape = {
    id?: number;
    nome?: string;
    imagem?: string;
    formt_id?: number;
    total_capitulos?: number | null;
    total_lidos?: number | null;
    ultimo_cap_numero?: number | string;
    ultimo_cap_id?: number;
    capitulo_min_num?: number | null;
    capitulo_max_num?: number | null;
    obra?: { id?: number; nome?: string; imagem?: string; total_capitulos?: number; capitulos?: unknown[] };
    ultimo_lido?: CapituloResumo & {
        prox_capitulo?: number | null;
        capitulo_id?: number;
        prox_capitulo_num?: number | string;
    };
    proximo_lido?: CapituloResumo | null;
};

function numFromReading(v: unknown): number {
    const n = parseFloat(String(v ?? ''));
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

function continueProgressLabelPct(pct: number): number {
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    if (pct >= 100) return 100;
    return Math.floor(pct);
}

function getContinueReadingProgressPct(
    it: ContinueItemShape,
    obra: ContinueItemShape['obra'] | Record<string, unknown>,
): number {
    const o = obra as { total_capitulos?: number | null; capitulos?: unknown[] };
    const totalCapRaw = it.total_capitulos ?? o.total_capitulos ?? (Array.isArray(o.capitulos) ? o.capitulos.length : null);
    let totalCap = numFromReading(totalCapRaw);
    const ultimo = it.ultimo_lido;
    let readThrough = numFromReading(it.total_lidos ?? ultimo?.cap_num ?? ultimo?.numero ?? it.ultimo_cap_numero);
    const prox = it.proximo_lido;
    if (prox != null) {
        const nextN = numFromReading(prox.cap_num ?? prox.numero);
        if (nextN > 1) readThrough = Math.max(readThrough, nextN - 1);
        if (nextN > totalCap) totalCap = nextN;
    }

    const hasChapterRange = it.capitulo_min_num != null && it.capitulo_max_num != null;
    const minChapterNum = numFromReading(it.capitulo_min_num);
    const maxChapterNum = numFromReading(it.capitulo_max_num);
    if (hasChapterRange && maxChapterNum >= minChapterNum && readThrough >= minChapterNum) {
        const publishedRange = maxChapterNum - minChapterNum + 1;
        const readRange = Math.min(maxChapterNum, readThrough) - minChapterNum + 1;
        if (publishedRange > 0) return Math.min(100, (readRange / publishedRange) * 100);
    }

    if (totalCap > 0) {
        return Math.min(100, (readThrough / totalCap) * 100);
    }

    if (ultimo) {
        const hasLegacyNextField =
            Object.prototype.hasOwnProperty.call(ultimo, 'prox_capitulo') ||
            Object.prototype.hasOwnProperty.call(ultimo, 'capitulo_id');
        const legacyComplete =
            hasLegacyNextField &&
            ultimo.prox_capitulo == null &&
            (ultimo.capitulo_id != null || it.ultimo_cap_id != null);
        if (legacyComplete) return 100;
    }
    return 0;
}

function resolveContinueCap(it: ContinueItemShape): { capId: number; capNum: string | number | undefined } | null {
    const prox = it.proximo_lido;
    if (prox != null && prox.cap_id != null && Number(prox.cap_id) > 0) {
        return {
            capId: Number(prox.cap_id),
            capNum: prox.cap_num ?? prox.numero,
        };
    }
    const u = it.ultimo_lido as ContinueItemShape['ultimo_lido'] & {
        prox_capitulo?: number | null;
        capitulo_id?: number;
        prox_capitulo_num?: number | string;
    };
    if (u) {
        const legacyId = u.prox_capitulo ?? u.capitulo_id;
        if (legacyId != null && Number(legacyId) > 0) {
            return {
                capId: Number(legacyId),
                capNum: u.prox_capitulo_num ?? u.cap_num ?? u.numero,
            };
        }
    }
    return null;
}

function homeContinueRowCardKey(item: unknown): string {
    const it = item as ContinueItemShape;
    const obra = (it.obra ?? it) as ContinueItemShape['obra'] & Record<string, unknown>;
    const obrId = obra?.id ?? it.id;
    const capId = resolveContinueCap(it)?.capId;
    return `${obrId ?? 'x'}-${capId ?? 'obra'}`;
}

function ContinueLendoHomeRowCard({
    item,
    accent,
    armedCardKey,
    setArmedCardKey,
    navigate,
}: {
    item: unknown;
    accent: string;
    armedCardKey: string | null;
    setArmedCardKey: (k: string | null) => void;
    navigate: NavigateFunction;
}) {
    const it = item as ContinueItemShape;
    const obra = (it.obra ?? it) as ContinueItemShape['obra'] & Record<string, unknown>;
    const obrId = obra?.id ?? it.id;
    const nextCap = resolveContinueCap(it);
    const capId = nextCap?.capId;
    const capNum = nextCap?.capNum;
    const pct = getContinueReadingProgressPct(it, obra);
    if (obrId == null) return null;
    const img =
        getObraImageUrl(Number(obrId), (obra as { imagem?: string }).imagem ?? it.imagem, 220) || FALLBACK_IMAGE_URL;
    const completed = capId == null && pct >= 100;
    const missingNextCap = capId == null && !completed;
    const cardKey = `${obrId}-${capId ?? 'obra'}`;
    const armed = armedCardKey === cardKey;
    const onPrimaryClick = () => {
        if (completed || missingNextCap) {
            navigate(generateWorkLink(Number(obrId)));
            setArmedCardKey(null);
            return;
        }
        if (armed) {
            navigate(generateWorkLink(Number(obrId)));
            setArmedCardKey(null);
            return;
        }
        setArmedCardKey(cardKey);
    };
    return (
        <div
            className="group flex h-full w-[160px] min-w-[160px] max-w-[160px] shrink-0 cursor-pointer flex-col rounded-xl transition-all duration-200 ease-out hover:z-[5] hover:-translate-y-1.5 hover:shadow-xl hover:shadow-black/50"
            style={{ ['--accent' as string]: accent } as CSSProperties}
        >
            <button
                type="button"
                onClick={onPrimaryClick}
                className="relative mb-3 aspect-[3/4] w-full shrink-0 cursor-pointer overflow-hidden rounded-xl transition-transform duration-200 ease-out active:scale-[0.98]"
            >
                <img
                    src={img}
                    alt=""
                    onError={(e) => applyFallbackImageOnError(e.currentTarget)}
                    loading="eager"
                    decoding="async"
                    className="h-full w-full object-cover transition-[transform,filter] duration-300 ease-out group-hover:scale-[1.04] group-hover:brightness-110"
                />
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-1/2 bg-gradient-to-t from-black/95 via-black/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 z-20 p-2">
                    <div className="mb-1 h-1.5 w-full rounded-full bg-white/20">
                        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: accent }} />
                    </div>
                    <p className="text-right text-xs font-medium text-white/90">{continueProgressLabelPct(pct)}%</p>
                </div>
            </button>
            <button
                type="button"
                onClick={onPrimaryClick}
                className="mb-2 flex min-h-[2rem] cursor-pointer items-start text-left transition-colors duration-200 ease-out active:opacity-90"
            >
                <h3 className="line-clamp-2 text-xs font-bold leading-tight text-white transition-colors group-hover:[color:var(--accent)]">
                    {(obra as { nome?: string }).nome ?? it.nome}
                </h3>
            </button>
            {completed || missingNextCap ? (
                <button
                    type="button"
                    onClick={() => navigate(generateWorkLink(Number(obrId)))}
                    className={`mt-auto w-full cursor-pointer rounded-lg py-1 -mx-1 px-1 text-left text-xs transition-colors ${
                        completed
                            ? 'text-emerald-300/90 hover:bg-emerald-500/10 hover:text-emerald-200'
                            : 'text-amber-200/90 hover:bg-amber-500/10 hover:text-amber-100'
                    }`}
                    style={{ outlineColor: accent }}
                >
                    {completed ? '100% lido · Ver obra' : 'Ver obra para continuar'}
                </button>
            ) : armed ? (
                <button
                    type="button"
                    onClick={() => {
                        if (capId != null) navigate(generateChapterLink(capId));
                        setArmedCardKey(null);
                    }}
                    className="continue-cap-reveal group/cap mt-auto flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs transition-all duration-300 hover:brightness-110"
                    style={{
                        backgroundColor: rgbaFromHex(accent, 0.18),
                        borderColor: rgbaFromHex(accent, 0.45),
                    }}
                >
                    <span className="text-sm font-medium text-white/90 transition-colors group-hover/cap:text-white">
                        Ler capítulo {capNum ?? '?'}
                    </span>
                </button>
            ) : (
                <button
                    type="button"
                    onClick={onPrimaryClick}
                    className="mt-auto w-full cursor-pointer rounded-lg py-1 -mx-1 px-1 text-left text-xs text-white/65 transition-colors hover:text-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 active:text-white group-hover:text-white/90"
                    style={{ outlineColor: accent }}
                >
                    Toque na obra para ver o capítulo
                </button>
            )}
        </div>
    );
}

function isStillContinueReadingItem(item: unknown): boolean {
    const row = item as ContinueItemShape;
    if (resolveContinueCap(row) == null) {
        const obra = (row.obra ?? row) as ContinueItemShape['obra'] | Record<string, unknown>;
        return getContinueReadingProgressPct(row, obra) < 100;
    }
    const proxId = row.proximo_lido?.cap_id;
    if (proxId != null && Number(proxId) > 0) {
        return true;
    }
    const obra = (row.obra ?? row) as { total_capitulos?: number | null };
    const totalRaw = row.total_capitulos ?? obra?.total_capitulos;
    const total = numFromReading(totalRaw);
    const lidos = numFromReading(row.total_lidos ?? row.ultimo_lido?.cap_num ?? row.ultimo_cap_numero);
    if (total > 0 && lidos >= total) return false;
    return true;
}

function MobileContinueSkeleton() {
    return (
        <div className="flex gap-4 overflow-hidden pb-4">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex w-[160px] min-w-[160px] max-w-[160px] shrink-0 flex-col gap-2">
                    <div className="aspect-[3/4] w-full animate-pulse rounded-xl bg-white/[0.08]" />
                    <div className="h-8 w-full animate-pulse rounded bg-white/[0.06]" />
                    <div className="h-8 w-full animate-pulse rounded-lg bg-white/[0.06]" />
                </div>
            ))}
        </div>
    );
}

export default function ContinueLendo({
    formatoId = 5,
    showAll = false,
    searchQuery = '',
    pagina = 1,
}: ContinueLendoProps) {
    const navigate = useNavigate();
    const [obras, setObras] = useState<unknown[]>([]);
    const [loading, setLoading] = useState(!showAll);
    const [homeRowLimit, setHomeRowLimit] = useState(HOME_ROW_LIMIT_NARROW);
    const [armedCardKey, setArmedCardKey] = useState<string | null>(null);
    const [scrollEdgeFades, setScrollEdgeFades] = useState({ left: false, right: false });
    const [swiperEdgeFades, setSwiperEdgeFades] = useState({ left: false, right: false });
    const homeScrollRef = useRef<HTMLDivElement>(null);
    const homeSwiperRef = useRef<{ slidePrev: () => void; slideNext: () => void; isBeginning: boolean; isEnd: boolean } | null>(
        null,
    );
    const [totalPages, setTotalPages] = useState(1);
    const [removingObrId, setRemovingObrId] = useState<number | null>(null);
    const [confirmRemoveObrId, setConfirmRemoveObrId] = useState<number | null>(null);
    const [swipeOffsets, setSwipeOffsets] = useState<Record<number, number>>({});
    const swipeRef = useRef<{
        obrId: number;
        startX: number;
        startY: number;
        active: boolean;
        lastOffset: number;
    } | null>(null);

    useLayoutEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const apply = () => setHomeRowLimit(mq.matches ? HOME_ROW_LIMIT_WIDE : HOME_ROW_LIMIT_NARROW);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);

    const accentFormato = formatoId > 0 ? formatoId : HOME_FORMATO_PADRAO;
    const accent = accentHexForFormato(accentFormato);

    const displayObras = useMemo(() => {
        const list = Array.isArray(obras) ? obras : [];
        if (showAll) return list;
        return list.filter((item) => isStillContinueReadingItem(item));
    }, [obras, showAll]);

    const updateHomeScrollFades = useCallback(() => {
        const el = homeScrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const maxScroll = Math.max(0, scrollWidth - clientWidth);
        const eps = 8;
        const hasOverflow = maxScroll > eps;
        const awayFromStart = scrollLeft > eps;
        const awayFromEnd = scrollLeft < maxScroll - eps;
        setScrollEdgeFades((prev) => {
            const next = {
                left: awayFromStart,
                right: hasOverflow && awayFromEnd,
            };
            if (prev.left === next.left && prev.right === next.right) return prev;
            return next;
        });
    }, []);

    const updateHomeSwiperFades = useCallback((sw: { isBeginning: boolean; isEnd: boolean } | null) => {
        if (!sw) return;
        setSwiperEdgeFades((prev) => {
            const next = {
                left: !sw.isBeginning,
                right: !sw.isEnd,
            };
            if (prev.left === next.left && prev.right === next.right) return prev;
            return next;
        });
    }, []);

    useEffect(() => {
        setScrollEdgeFades({ left: false, right: false });
        setSwiperEdgeFades({ left: false, right: false });
    }, [homeRowLimit]);

    useEffect(() => {
        if (loading) {
            setScrollEdgeFades({ left: false, right: false });
            setSwiperEdgeFades({ left: false, right: false });
        }
    }, [loading]);

    useEffect(() => {
        if (showAll || loading) return;
        const el = homeScrollRef.current;
        if (!el) return;
        const tick = () => updateHomeScrollFades();
        tick();
        el.addEventListener('scroll', tick, { passive: true });
        const ro = new ResizeObserver(tick);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', tick);
            ro.disconnect();
        };
    }, [showAll, loading, displayObras.length, homeRowLimit, updateHomeScrollFades]);

    useEffect(() => {
        if (showAll || loading) return;
        const sw = homeSwiperRef.current;
        if (!sw) return;
        const id = requestAnimationFrame(() => {
            (sw as { update?: () => void }).update?.();
            updateHomeSwiperFades(sw);
        });
        return () => cancelAnimationFrame(id);
    }, [displayObras, showAll, loading, updateHomeSwiperFades]);

    const fetchReadings = async () => {
        setLoading(true);
        try {
            if (showAll) {
                const qs = new URLSearchParams({
                    limite: String(SHOW_ALL_LIMITE),
                    pagina: String(Math.max(1, pagina)),
                });
                if (formatoId > 0) qs.set('formato', String(formatoId));
                const q = searchQuery.trim();
                if (q) qs.set('q', q.slice(0, 80));
                const res = await api.get(`readings/continue/all?${qs.toString()}`);
                const { items, totalPages: tp } = parseContinueReadingList(res);
                setObras(items);
                setTotalPages(tp);
            } else {
                const qs = new URLSearchParams({ limit: '40' });
                if (formatoId > 0) qs.set('formato', String(formatoId));
                const res = await api.get(`readings/continue?${qs.toString()}`);
                const parsed = parseContinueReadingList(res);
                setObras(parsed.items);
                setTotalPages(parsed.totalPages);
            }
        } catch (error) {
            console.error('Erro ao buscar continue lendo:', error);
            setObras([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            void fetchReadings();
        }, showAll ? 0 : 120);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchReadings fecha sobre estado atual
    }, [formatoId, showAll, searchQuery, pagina]);

    async function removeReadingForObraFromContinue(obrId: number): Promise<void> {
        if (!Number.isInteger(obrId) || obrId < 1) return;
        if (removingObrId != null) return;
        try {
            setRemovingObrId(obrId);
            await api.delete(`readings/${obrId}`);
            setObras((prev) =>
                prev.filter((item) => {
                    const it = item as ContinueItemShape;
                    const obra = (it.obra ?? it) as ContinueItemShape['obra'];
                    const id = obra?.id ?? it.id;
                    return Number(id) !== obrId;
                }),
            );
            if (confirmRemoveObrId === obrId) setConfirmRemoveObrId(null);
        } catch (err) {
            console.error('Erro ao apagar leitura da obra', err);
            alert('Não foi possível apagar a leitura.');
        } finally {
            setRemovingObrId(null);
        }
    }

    function beginSwipe(e: ReactPointerEvent<HTMLDivElement>, obrId: number): void {
        if (!showAll || removingObrId != null) return;
        swipeRef.current = { obrId, startX: e.clientX, startY: e.clientY, active: false, lastOffset: 0 };
    }

    function moveSwipe(e: ReactPointerEvent<HTMLDivElement>, obrId: number): void {
        const s = swipeRef.current;
        if (!showAll || !s || s.obrId !== obrId || removingObrId != null) return;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        if (!s.active) {
            if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
                swipeRef.current = null;
                return;
            }
            if (Math.abs(dx) < 12) return;
            s.active = true;
        }
        const limited = Math.max(-140, Math.min(140, dx));
        s.lastOffset = limited;
        setSwipeOffsets((prev) => ({ ...prev, [obrId]: limited }));
    }

    function endSwipe(obrId: number): void {
        const offset = swipeRef.current?.obrId === obrId ? swipeRef.current.lastOffset : (swipeOffsets[obrId] ?? 0);
        swipeRef.current = null;
        setSwipeOffsets((prev) => ({ ...prev, [obrId]: 0 }));
        if (showAll && Math.abs(offset) >= 110 && removingObrId == null) {
            void removeReadingForObraFromContinue(obrId);
        }
    }

    const showVerTodos =
        !showAll && !loading && displayObras.length > 0 && (displayObras.length > homeRowLimit || totalPages > 1);

    if (showAll) {
        if (loading && obras.length === 0) {
            return (
                <div className="space-y-3 pb-8">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex w-full overflow-hidden rounded-xl border border-white/5 bg-neutral-900/40"
                        >
                            <div className="aspect-[3/4] w-20 shrink-0 animate-pulse bg-white/[0.08] md:w-24" />
                            <div className="min-w-0 flex-1 space-y-2 p-3">
                                <div className="h-4 w-[75%] animate-pulse rounded bg-white/[0.08]" />
                                <div className="h-4 w-[45%] animate-pulse rounded bg-white/[0.08]" />
                                <div className="h-9 max-w-xs animate-pulse rounded-lg bg-white/[0.08]" />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (!loading && displayObras.length === 0) {
            return (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-12 text-center text-white/55">
                    Nenhuma obra neste filtro.
                </div>
            );
        }

        const extraParams: Record<string, string | number> = {};
        if (formatoId > 0) extraParams.formato = formatoId;
        const q = searchQuery.trim();
        if (q) extraParams.q = q;

        return (
            <section className="pb-8">
                <div className="flex flex-col gap-3">
                    {displayObras.map((item: unknown) => {
                        const it = item as ContinueItemShape;
                        const obra = (it.obra ?? it) as ContinueItemShape['obra'] & Record<string, unknown>;
                        const obrId = obra?.id ?? it.id;
                        const nextCap = resolveContinueCap(it);
                        const capId = nextCap?.capId;
                        const capNum = nextCap?.capNum;
                        const pct = getContinueReadingProgressPct(it, obra);
                        if (obrId == null) return null;
                        const img =
                            getObraImageUrl(Number(obrId), (obra as { imagem?: string }).imagem ?? it.imagem, 260) ||
                            FALLBACK_IMAGE_URL;
                        const completed = capId == null && pct >= 100;
                        const missingNextCap = capId == null && !completed;
                        const cardKey = `${obrId}-${capId ?? 'concluido'}`;
                        const armed = armedCardKey === cardKey;
                        const obraIdNum = Number(obrId);
                        const swipeOffset = Number.isFinite(obraIdNum) ? (swipeOffsets[obraIdNum] ?? 0) : 0;
                        const onPrimaryClick = () => {
                            if (completed || missingNextCap) {
                                navigate(generateWorkLink(Number(obrId)));
                                setArmedCardKey(null);
                                return;
                            }
                            if (armed) {
                                navigate(generateWorkLink(Number(obrId)));
                                setArmedCardKey(null);
                                return;
                            }
                            setArmedCardKey(cardKey);
                        };
                        return (
                            <div key={cardKey} className="relative overflow-hidden rounded-xl">
                                <div
                                    className={`absolute inset-y-0 flex w-28 items-center justify-center rounded-xl bg-red-500/18 text-red-200 transition-opacity ${
                                        Math.abs(swipeOffset) > 8 ? 'opacity-100' : 'opacity-0'
                                    } ${swipeOffset < 0 ? 'right-0' : 'left-0'}`}
                                    aria-hidden
                                >
                                    <Trash2 size={24} />
                                </div>
                                <div
                                    className="group relative flex w-full overflow-hidden rounded-xl border border-transparent bg-neutral-900/40 transition-all duration-300 hover:border-white/10 hover:shadow-lg"
                                    onPointerDown={(e) => {
                                        if (Number.isFinite(obraIdNum)) beginSwipe(e, obraIdNum);
                                    }}
                                    onPointerMove={(e) => {
                                        if (Number.isFinite(obraIdNum)) moveSwipe(e, obraIdNum);
                                    }}
                                    onPointerUp={() => {
                                        if (Number.isFinite(obraIdNum)) endSwipe(obraIdNum);
                                    }}
                                    onPointerCancel={() => {
                                        if (Number.isFinite(obraIdNum)) endSwipe(obraIdNum);
                                    }}
                                    style={
                                        {
                                            transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined,
                                            touchAction: 'pan-y',
                                            ['--accent' as string]: accent,
                                        } as CSSProperties
                                    }
                                >
                                    {completed && (
                                        <div className="pointer-events-none absolute -right-10 -top-8 z-0 h-40 w-52 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.42),rgba(16,185,129,0.18)_38%,rgba(16,185,129,0.06)_62%,transparent_78%)]" />
                                    )}
                                    <button
                                        type="button"
                                        onClick={onPrimaryClick}
                                        className="relative aspect-[3/4] w-20 shrink-0 overflow-hidden transition-transform duration-100 active:scale-95 md:w-24"
                                    >
                                        <img
                                            src={img}
                                            alt=""
                                            onError={(e) => applyFallbackImageOnError(e.currentTarget)}
                                            loading="lazy"
                                            decoding="async"
                                            fetchPriority="low"
                                            className="h-full w-full object-cover"
                                        />
                                        <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-1/2 bg-gradient-to-t from-black/95 via-black/70 to-transparent" />
                                        <div className="absolute bottom-0 left-0 right-0 z-20 p-2">
                                            <div className="mb-1 h-1.5 w-full rounded-full bg-white/20">
                                                <div
                                                    className="h-1.5 rounded-full"
                                                    style={{
                                                        width: `${pct}%`,
                                                        backgroundColor: completed ? '#10b981' : accent,
                                                    }}
                                                />
                                            </div>
                                            <p className="text-right text-xs font-medium text-white/90">
                                                {continueProgressLabelPct(pct)}%
                                            </p>
                                        </div>
                                    </button>
                                    <div className="relative z-10 flex min-w-0 flex-1 flex-col justify-between p-3">
                                        <div className="mb-2 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (obrId != null) setConfirmRemoveObrId(Number(obrId));
                                                }}
                                                disabled={removingObrId === Number(obrId)}
                                                className="rounded-md border border-red-400/30 px-2 py-1 text-[11px] text-red-300 transition-colors hover:border-red-300/50 hover:bg-red-500/10 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {removingObrId === Number(obrId) ? 'A apagar…' : 'Apagar leitura'}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onPrimaryClick}
                                            className="text-left transition-transform duration-100 active:scale-[0.98]"
                                        >
                                            <h3 className="mb-2 line-clamp-2 text-xs font-bold leading-tight text-white transition-colors group-hover:[color:var(--accent)] md:text-sm">
                                                {(obra as { nome?: string }).nome ?? it.nome}
                                            </h3>
                                        </button>
                                        {completed || missingNextCap ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(generateWorkLink(Number(obrId)))}
                                                className={`w-full rounded-lg py-1.5 -mx-1 px-1 text-left text-xs transition-colors ${
                                                    completed
                                                        ? 'text-emerald-300/90 hover:bg-emerald-500/10 hover:text-emerald-200'
                                                        : 'text-amber-200/90 hover:bg-amber-500/10 hover:text-amber-100'
                                                }`}
                                            >
                                                {completed ? '100% lido · Ver obra' : 'Ver obra para continuar'}
                                            </button>
                                        ) : armed ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (capId != null) navigate(generateChapterLink(capId));
                                                    setArmedCardKey(null);
                                                }}
                                                className="continue-cap-reveal group/cap flex items-center justify-between rounded-lg border px-2 py-1.5 text-xs transition-all duration-300 hover:brightness-110"
                                                style={{
                                                    backgroundColor: rgbaFromHex(accent, 0.18),
                                                    borderColor: rgbaFromHex(accent, 0.45),
                                                }}
                                            >
                                                <span className="text-sm font-medium text-white/90 transition-colors group-hover/cap:text-white">
                                                    Ler capítulo {capNum ?? '?'}
                                                </span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={onPrimaryClick}
                                                className="w-full rounded-lg py-1 -mx-1 px-1 text-left text-xs text-white/65 transition-colors hover:text-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 active:text-white"
                                                style={{ outlineColor: accent }}
                                            >
                                                Toque na obra para ver o capítulo
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {totalPages > 1 && (
                    <div className="mt-6 flex justify-center">
                        <Pagination
                            currentPage={pagina}
                            totalPages={totalPages}
                            baseUrl="/continue-lendo"
                            extraParams={extraParams}
                        />
                    </div>
                )}

                {confirmRemoveObrId != null && (
                    <div
                        className="fixed inset-0 z-[220] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
                        onClick={() => {
                            if (removingObrId == null) setConfirmRemoveObrId(null);
                        }}
                        aria-modal
                        role="dialog"
                        aria-labelledby="continue-remove-title"
                    >
                        <div
                            className="w-full max-w-sm rounded-2xl border border-white/10 bg-neutral-900 p-4 shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 id="continue-remove-title" className="text-base font-semibold text-white">
                                Apagar leitura desta obra?
                            </h3>
                            <p className="mt-2 text-sm text-white/75">
                                Todo o progresso nesta obra será apagado — fica como se nunca tivesses lido nenhum
                                capítulo. Isto não remove dos favoritos.
                            </p>
                            <div className="mt-4 flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setConfirmRemoveObrId(null)}
                                    disabled={removingObrId != null}
                                    className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/80 transition-colors hover:border-white/30 hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void removeReadingForObraFromContinue(confirmRemoveObrId)}
                                    disabled={removingObrId != null}
                                    className="rounded-lg border border-red-500 bg-red-600 px-3 py-1.5 text-sm text-white transition-colors hover:border-red-400 hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {removingObrId != null ? 'A apagar…' : 'Apagar leitura'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        );
    }

    if (!loading && displayObras.length === 0) return null;

    return (
        <section className="relative min-w-0 max-w-full animate-fadeIn pb-4 md:pb-8">
            <div className="mb-6 flex items-center justify-between gap-3 px-4 md:px-6">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
                    <BookMarked className="h-5 w-5 shrink-0" style={{ color: accent }} />
                    <h2 className="relative inline-block shrink-0 text-2xl font-bold text-white">
                    Continue Lendo
                        <span
                            className="absolute -bottom-2 left-0 h-1 w-1/3 rounded-full"
                            style={{ backgroundColor: accent }}
                        />
                    </h2>
                    {showVerTodos && (
                        <button
                            type="button"
                            onClick={() => navigate(`/continue-lendo?formato=${formatoId}`)}
                            className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/80 transition-all duration-200 ease-out hover:scale-105 hover:border-white/35 hover:bg-white/5 hover:text-white active:scale-100"
                        >
                            <List className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            Ver todos
                        </button>
                    )}
                </div>

                {!loading && displayObras.length > 0 && (
                    <div className="hidden shrink-0 items-center gap-2 md:flex">
                        <button type="button" onClick={() => homeSwiperRef.current?.slidePrev()} className={navBtnClass} title="Anterior">
                            <ChevronLeft size={20} />
                        </button>
                        <button type="button" onClick={() => homeSwiperRef.current?.slideNext()} className={navBtnClass} title="Próximo">
                            <ChevronRight size={20} />
                        </button>
                    </div>
                )}
            </div>

            {loading && (
                <div className="relative -mx-4 py-3 md:-mx-8">
                    <div className="pl-4 pr-2 sm:pr-4 md:pl-8 md:pr-6">
                        <MobileContinueSkeleton />
                    </div>
                </div>
            )}

            {!loading && displayObras.length > 0 && (
                <>
                    <div className="md:hidden">
                        <div className="relative -mx-4 py-3 md:-mx-8">
                            {scrollEdgeFades.left && (
                                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-14 bg-gradient-to-r from-[#000000] to-transparent md:w-16" />
                            )}
                            {scrollEdgeFades.right && (
                                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-14 bg-gradient-to-l from-[#000000] to-transparent md:w-16" />
                            )}
                            <div
                                ref={homeScrollRef}
                                className="continue-lendo-home-scroll overflow-x-auto pl-4 pb-4 pt-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] sm:pr-4 md:pl-8 md:pr-6 [&::-webkit-scrollbar]:hidden"
                            >
                                <div className="flex gap-4 sm:gap-6">
                                    {displayObras.slice(0, homeRowLimit).map((item: unknown) => (
                                        <ContinueLendoHomeRowCard
                                            key={homeContinueRowCardKey(item)}
                                            item={item}
                                            accent={accent}
                                            armedCardKey={armedCardKey}
                                            setArmedCardKey={setArmedCardKey}
                                            navigate={navigate}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hidden md:block">
                        <div className="relative -mx-4 py-3 md:-mx-8">
                            {swiperEdgeFades.left && (
                                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-14 bg-gradient-to-r from-[#000000] to-transparent md:w-16" />
                            )}
                            {swiperEdgeFades.right && (
                                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-14 bg-gradient-to-l from-[#000000] to-transparent md:w-16" />
                            )}
                            <div className="pl-4 pr-4 md:pl-8 md:pr-8">
                                <Swiper
                                    onSwiper={(sw) => {
                                        homeSwiperRef.current = sw;
                                        updateHomeSwiperFades(sw);
                                    }}
                                    onProgress={(sw) => updateHomeSwiperFades(sw)}
                                    modules={[FreeMode, Navigation]}
                                    slidesPerView="auto"
                                    spaceBetween={24}
                                    freeMode
                                    grabCursor
                                    className="continue-lendo-home-swiper w-full !pb-4"
                                >
                                {displayObras.slice(0, HOME_ROW_LIMIT_WIDE).map((item: unknown) => (
                                    <SwiperSlide
                                        key={homeContinueRowCardKey(item)}
                                        className="!h-auto !w-[160px] shrink-0 !overflow-visible !py-3"
                                    >
                                        <ContinueLendoHomeRowCard
                                            item={item}
                                            accent={accent}
                                            armedCardKey={armedCardKey}
                                            setArmedCardKey={setArmedCardKey}
                                            navigate={navigate}
                                        />
                                </SwiperSlide>
                                ))}
                                </Swiper>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}
